import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Copy, Check, Link as LinkIcon } from "lucide-react";
import { toast } from "@/hooks/use-toast";

interface ReferralLinkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  referralCode: string;
  userName?: string;
}

export function ReferralLinkDialog({
  open,
  onOpenChange,
  referralCode,
  userName
}: ReferralLinkDialogProps) {
  const [copied, setCopied] = useState(false);
  
  const referralLink = `${window.location.origin}/register?ref=${referralCode}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      toast({
        title: "Copied!",
        description: "Invitation link copied to clipboard",
      });
      
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to copy link to clipboard",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LinkIcon className="h-5 w-5" />
            Invitation Link Generated
          </DialogTitle>
          <DialogDescription>
            {userName ? `Share this link with ${userName}'s friends to invite them to Subtrackr` : 
             "Share this link to invite friends to Subtrackr"}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Referral Code</label>
            <Input
              value={referralCode}
              readOnly
              className="font-mono"
            />
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium">Invitation Link</label>
            <div className="flex gap-2">
              <Input
                value={referralLink}
                readOnly
                className="font-mono text-sm"
              />
              <Button
                onClick={handleCopy}
                variant="outline"
                size="icon"
                className="shrink-0"
              >
                {copied ? (
                  <Check className="h-4 w-4 text-green-500" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
          
          <div className="bg-muted p-3 rounded-md text-sm text-muted-foreground">
            <p>
              When someone registers using this link, they will be automatically 
              linked to {userName ? `${userName}` : "the referrer"} in the system.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
